<template>
  <v-container fluid v-if="initialized">
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('route', 2)}}</h2>
      <v-btn
        v-if="userLoaded && user.can('create_route')"
        outlined
        color="primary"
        to="/route/create">
        <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
        {{ $t('new_route' )}}
      </v-btn>
    </div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-text-field
            :label="$t('route_name')"
            v-model="searchCriteria.name"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-road-variant">
          </v-text-field>
        </v-col>
        <v-col class="d-flex align-center" xl="3" lg="3" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.startedAfter"
            :timezone="customerTimezone"
            :label="$t('start_date')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.startedBefore"
            :timezone="customerTimezone"
            prepend-inner-icon="mdi-calendar"
            :label="$t('end_date')"
            reset-on-timezone-change
            end-of-day />
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('vehicle', 2)"
            v-model="searchCriteria.vehicles"
            :items="vehicleOptions"
            prepend-inner-icon="mdi-truck"/>
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-text-field
            :label="$t('location_code')"
            @keyup.enter="initiateSearch()"
            v-model="searchCriteria.locationCode"
            prepend-inner-icon="mdi-map-marker">
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col xl="2" lg="2" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('product', 2)"
            v-model="searchCriteria.products"
            :items="productOptions"
            prepend-inner-icon="mdi-silverware-fork-knife"/>
        </v-col>
        <v-col xl="2" lg="2" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('violation', 2)"
            v-model="searchCriteria.violations"
            :items="violationOptions"
            prepend-inner-icon="mdi-alert"/>
        </v-col>
        <v-col xl="2" lg="2" md="4" cols="12">
          <v-select
            :label="$t('violation_level')"
            v-model="searchCriteria.violationLevel"
            :items="violationLevelOptions"
            clearable
            prepend-inner-icon="mdi-alert-circle"/>
        </v-col>
        <v-col xl="2" lg="2" md="4" cols="12">
          <v-select
            :label="$t('violation_duration')"
            v-model="searchCriteria.violationDuration"
            :items="violationDurationOptions"
            prepend-inner-icon="mdi-clock"/>
        </v-col>
        <v-col xl="2" lg="2" md="4" cols="12">
          <v-select
            :label="$t('exclude_deleted')"
            v-model="searchCriteria.deletedRoutes"
            :items="deletedRouteOptions"
            prepend-inner-icon="mdi-trash-can"/>
        </v-col>
        <v-col xl="2" lg="2" md="4" cols="12">
          <v-select
            :label="$t('only_late_violations')"
            v-model="searchCriteria.excludeOnTimeViolations"
            :items="lateViolationOptions"
            prepend-inner-icon="mdi-trash-can"/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <div v-if="hasForecastingEnabled" class="d-flex justify-end align-center mb-1">
      <span class="mr-2">{{ $t('forecast_violation_legend') }}</span>
      <span class="mr-1">
        <icon-tooltip color="temperature-cold" icon="mdi-weather-cloudy-alert" :tooltip="$t('forecast_cold_violation')"/>
      </span>
      <icon-tooltip color="temperature-hot" icon="mdi-weather-cloudy-alert" :tooltip="$t('forecast_hot_violation')"/>
    </div>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table route-index-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        :item-class="rowClassCallback"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <!-- Only a stateless button lives in the header: Vuetify renders the
             header `<th>`s without keys, so this node is destroyed and
             re-created whenever a column is hidden or shown. The configurator
             itself is mounted below, outside the table, so the open dialog
             survives a toggle. -->
        <template #header.__columnConfigurator>
          <v-btn
            icon
            small
            class="column-configurator-activator"
            :aria-label="$t('configure_columns')"
            @click.stop="openColumnConfigurator">
            <v-icon small>mdi-cog</v-icon>
          </v-btn>
        </template>
        <template #item.name="row">
          <router-link :to="`/route/${row.item.id}`">
            <v-icon small color="primary" class="mr-1">mdi-road-variant</v-icon>
            {{ row.item.name }}
            <span v-if="row.item.deleted">({{ $t('deleted') }})</span>
          </router-link>
        </template>
        <template #item.duration="row">
          <span v-if="row.item.duration">{{ row.item.duration | humanizeDuration({ numberOfSignificantParts: 2, separator: ', ' }) }}</span>
          <span v-else class="not-set">{{ $t('not_set') }}</span>
        </template>
        <template #item.start="row">
          <multi-time-display v-if="row.item.start" :time="row.item.start" />
          <span v-else class="not-set">{{ $t('not_set') }}</span>
        </template>
        <template #item.end="row">
          <if-set :value="row.item.end">
            <multi-time-display :time="row.item.end" />
            <template #not-set>
              <span v-if="!row.item.start" class="not-set">{{ $t('not_set') }}</span>
              <span v-else class="grey--text lighten-2">
                ({{ $t('in_progress') }})
              </span>
            </template>
          </if-set>
        </template>
        <template #item.vehicle="row">
          <router-link v-if="row.item.vehicle != null" :to="`/vehicle/${row.item.vehicle.id}`">
            <v-icon small color="primary" class="mr-1">mdi-truck</v-icon>{{row.item.vehicle.name}}
          </router-link>
          <a v-else
            class="font-italic"
            target="_blank"
            :href="`https://${zendeskDomain}/hc/en-us/articles/360042724133-Vehicle-Cannot-Be-Displayed`">
            {{ $t('cannot_be_displayed') }}
          </a>
        </template>
        <template #item.freezer_air="row">
          <if-set :value="latestAirTemperature(row.item, 'freezer')">
            <template v-slot="scope">
              {{ temperatureToCustomerScale(scope.value) }} &deg; {{ customerContext.tempFormat() }}
            </template>
            <template #not-set>
              <span class="not-set">N/A</span>
            </template>
          </if-set>
        </template>
        <template #item.cooler_air="row">
          <if-set :value="latestAirTemperature(row.item, 'cooler')">
            <template v-slot="scope">
              {{ temperatureToCustomerScale(scope.value) }} &deg; {{ customerContext.tempFormat() }}
            </template>
            <template #not-set>
              <span class="not-set">N/A</span>
            </template>
          </if-set>
        </template>
        <template v-for='(column, index) in routeProductColumns' #[`item.${column.value}`]="row">
          <route-product-status
            :route="row.item"
            :route-product="get(row.item.product_indexed_route_products, column.product_id)"
            :forecast-violation="routeProductForecastViolation(row.item, get(row.item.product_indexed_route_products, column.product_id))"
            :key="index"/>
        </template>
        <template #item.edit="row">
          <router-link :to="`/route/update/${row.item.id}`">
            <v-icon small class="mr-2">
              mdi-pencil
            </v-icon>
          </router-link>
        </template>
      </v-data-table>
      <!-- Mounted outside the table on purpose (see the header slot above):
           this node's position never changes, so the dialog stays open while
           the user toggles and reorders columns. Renders nothing while the
           feature flag is off. -->
      <column-configurator
        ref="columnConfigurator"
        hide-activator
        :table-id="tableId"
        :columns="resolvedColumns"
        :default-columns="defaultColumnRegistry"
        @update:columns="onRouteIndexColumnsUpdate"
        @reset="onRouteIndexColumnsReset" />
      <div class="mt-2">
        <p class="text-right mb-0">
          <span v-if="hasRouteCreationEnabled">{{ $t('automatic_route_creation_enabled') }}</span>
          <span v-else>{{ $t('automatic_route_creation_disabled') }}</span>
        </p>
      </div>
    </div>
  </v-container>
  <v-container v-else>
    <index-table-skeleton>
      <template v-slot:header>
        <h2>{{ $tc('route', 2)}}</h2>
      </template>
    </index-table-skeleton>
  </v-container>
</template>

<script>
import IndexTableSkeleton from '@/components/shared/IndexTableSkeleton'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import RouteProductStatus from '@/components/route/RouteProductStatus'
import IconTooltip from '@/components/shared/IconTooltip'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import QueryParamMixin from '@/components/mixins/QueryParamMixin'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import VfTooltip from '@/components/shared/VfTooltip'
import DatePicker from '@/components/shared/DatePicker'
import IfSet from '@/components/shared/IfSet'
import ColumnConfigurator, { COLUMN_CONFIGURATOR_HEADER } from '@/components/shared/ColumnConfigurator'
import TableColumnPreferencesMixin from '@/components/mixins/TableColumnPreferencesMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import { map, each, get, remove, indexOf, snakeCase, sortBy, some, filter } from 'lodash'

/**
 * Persistence contract for this table (epic VF-4136). Both values are FIXED:
 * the id is what saved preferences are keyed by, so renaming it orphans every
 * preference already stored, and the integer version is bumped only for a
 * deliberate migration of the registry below.
 */
export const ROUTE_INDEX_TABLE_ID = 'RouteIndex'
export const ROUTE_INDEX_COLUMNS_VERSION = 1

export default {
  name: 'RouteIndex',
  components: {
    IndexTableSkeleton,
    MultiTimeDisplay,
    RouteProductStatus,
    IconTooltip,
    DatePicker,
    TablePaginationControls,
    VfAutocomplete,
    VfTooltip,
    IfSet,
    ColumnConfigurator
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    DateTimeUtilsMixin,
    BrowserUtilitiesMixin,
    QueryParamMixin,
    TemperatureMixin,
    TableColumnPreferencesMixin
  ],
  data () {
    return {
      model: 'Route',
      searchCriteria: this.getDefaultSearchCriteria(),
      productOptions: [],
      productOptionsLoaded: false,
      vehicleOptions: [],
      vehicleOptionsLoaded: false,
      violationOptions: [],
      violationOptionsLoaded: false,
      violationLevelOptions: [],
      violationLevelOptionsLoaded: false,
      violationDurationOptions: [
        { text: this.$tc('unit_minutes', 30, { unit: 30 }), value: '30 minutes' },
        { text: this.$tc('unit_minutes', 40, { unit: 40 }), value: '40 minutes' },
        { text: this.$tc('unit_minutes', 60, { unit: 60 }), value: '60 minutes' },
        { text: this.$tc('unit_minutes', 90, { unit: 90 }), value: '90 minutes' },
        { text: this.$tc('unit_minutes', 120, { unit: 120 }), value: '120 minutes' }
      ],
      lateViolationOptions: [
        { text: this.$t('exclude_on_time_rcas'), value: 'true' },
        { text: this.$t('show_all'), value: 'false' }
      ],
      deletedRouteOptions: [
        {
          value: 'exclude',
          text: this.$t('exclude_deleted')
        },
        {
          value: 'include',
          text: this.$t('include_deleted')
        },
        {
          value: 'show_only',
          text: this.$t('only_show_deleted')
        }
      ],
      queryParams: [
        {
          name: 'name',
          type: String
        },
        {
          name: 'locationCode',
          type: String
        },
        {
          name: 'route_id',
          type: 'IDArray'
        },
        {
          name: 'startedAfter',
          type: 'Moment'
        },
        {
          name: 'startedBefore',
          type: 'Moment'
        },
        {
          name: 'vehicles',
          type: Array,
          validator: value => some(this.vehicleOptions, x => x.value === value)
        },
        {
          name: 'products',
          type: 'IDArray',
          validator: value => some(this.productOptions, x => x.value === value)
        },
        {
          name: 'violations',
          type: Array,
          validator: value => some(this.violationOptions, x => x.value === value)
        },
        {
          name: 'violationDuration',
          type: String,
          validator: value => some(this.violationDurationOptions, x => x.value === value)
        },
        {
          name: 'deletedRoutes',
          type: String,
          validator: value => some(this.deletedRouteOptions, x => x.value === value)
        }
      ]
    }
  },
  computed: {
    zendeskDomain () {
      return process.env.VUE_APP_ZENDESK_DOMAIN
    },
    hasForecastingEnabled () {
      return this.customerContext.getSetting('enableSimulatedTemperatureForecast') === 1
    },
    hasRouteCreationEnabled () {
      return get(this.customerContext.getSetting('processing.RouteDetection'), 'enableRouteStarting')
    },
    /**
     * Whether the options have been loaded yet
     * @returns boolean
     */
    initialized () {
      return this.vehicleOptionsLoaded && this.productOptionsLoaded && this.violationOptionsLoaded
    },
    /**
     * Build a list of route product columns to display in the table. This will get added to the base list of columns.
     * @returns Array
     */
    routeProductColumns () {
      const result = []
      // Build a map of product ids to product names by iterating through each route's route products.
      const productsMap = {}
      each(this.remoteData.items, route => {
        each(route.route_products, routeProduct => {
          productsMap[routeProduct.product_id] = get(routeProduct, 'product.name', this.$t('unknown'))
        })
      })
      // Create column entries
      each(productsMap, (name, id) => {
        result.push({
          text: name,
          product_id: id,
          value: `product_${id}`,
          sortable: false,
          align: 'end',
          class: 'wrap'
        })
      })
      return result
    },
    /**
     * @return String the id saved preferences for this table are keyed by
     */
    tableId () {
      return ROUTE_INDEX_TABLE_ID
    },

    /**
     * Whether the column registry is complete enough to be published to the
     * persistence layer. The product columns are derived from the routes in
     * the current result set, so before the first search resolves the registry
     * carries no product ids at all — publishing it then would make
     * convergeTableColumns() prune every saved product preference and write the
     * pruned form back on every visit to this page (VF-4201 behaviour).
     *
     * @return Boolean
     */
    columnPreferencesReady () {
      return this.remoteData.isLoaded
    },

    /**
     * This table's built-in column registry in default display order: Route
     * Name pinned first, the route's own columns, the opt-in air temperature
     * columns, one column per product in the current result set, and Edit
     * last. `id` always equals the Vuetify header `value`; product ids are
     * product_{product_id} (VF-4177) so a preference follows the product.
     * `sortable`, `align`, `headerClass` and `product_id` ride along as extra
     * fields — the configurator preserves unknown fields untouched.
     *
     * @return Array
     */
    defaultColumnRegistry () {
      const columns = [
        { id: 'name', label: this.$t('route_name'), visible: true, primary: true, hideable: false, sortable: true },
        { id: 'start', label: this.$t('start'), visible: true, primary: false, hideable: true, sortable: true },
        { id: 'end', label: this.$t('end'), visible: true, primary: false, hideable: true, sortable: true },
        { id: 'duration', label: this.$t('duration'), visible: true, primary: false, hideable: true },
        { id: 'vehicle', label: this.$tc('vehicle'), visible: true, primary: false, hideable: true },
        // VF-4176: the vehicle's last reported air readings. Hidden by default
        // so the table nobody configures is exactly the table we ship today.
        { id: 'freezer_air', label: this.$t('freezer_air'), visible: false, primary: false, hideable: true, align: 'end' },
        { id: 'cooler_air', label: this.$t('cooler_air'), visible: false, primary: false, hideable: true, align: 'end' }
      ]
      each(this.routeProductColumns, column => {
        columns.push({
          id: column.value,
          label: column.text,
          visible: true,
          primary: false,
          hideable: true,
          product_id: column.product_id,
          align: 'end',
          headerClass: 'wrap'
        })
      })
      // Edit is the only way into the edit form, so it is never hideable — but
      // it is not the primary either: the user may drag it anywhere except
      // position 0, which Route Name owns.
      columns.push({ id: 'edit', label: this.$t('edit'), visible: true, primary: false, hideable: false })
      return columns
    },

    /**
     * Override of TableColumnPreferencesMixin's computed: the single table this
     * component owns, published only once the registry is complete.
     *
     * @return Object
     */
    tableColumnDefaults () {
      if (!this.columnPreferencesReady) {
        return {}
      }
      return {
        [ROUTE_INDEX_TABLE_ID]: {
          version: ROUTE_INDEX_COLUMNS_VERSION,
          columns: this.defaultColumnRegistry
        }
      }
    },

    /**
     * The registry actually in effect: saved order/visibility merged into the
     * defaults, or the raw defaults when the feature flag is off or the first
     * search has not resolved yet.
     *
     * @return Array
     */
    resolvedColumns () {
      return this.resolvedTableColumns[ROUTE_INDEX_TABLE_ID] || this.defaultColumnRegistry
    },

    /**
     * Vuetify headers for the visible registry columns, in the effective order,
     * with the synthetic gear header appended when the feature is on (VF-4197).
     *
     * @return Array
     */
    tableFields () {
      const fields = []
      each(this.resolvedColumns, column => {
        if (!column.visible) {
          return
        }
        const field = { text: column.label, value: column.id, sortable: column.sortable === true }
        if (column.align !== undefined) {
          field.align = column.align
        }
        if (column.headerClass !== undefined) {
          field.class = column.headerClass
        }
        if (column.product_id !== undefined) {
          field.product_id = column.product_id
        }
        fields.push(field)
      })
      if (this.configurableTableColumnsEnabled && this.columnPreferencesReady) {
        fields.push(COLUMN_CONFIGURATOR_HEADER)
      }
      return fields
    }
  },
  methods: {
    get: get,
    /**
     * Open the configurator modal from the gear button rendered in the table
     * header. The modal itself lives outside the table (see the template) so it
     * is not re-created when the header row changes length.
     */
    openColumnConfigurator () {
      this.$refs.columnConfigurator.openDialog()
    },
    /**
     * Apply a column change coming from the configurator, then make sure the
     * server-side sort still points at a column the user can see (VF-4255).
     *
     * @param {Array} columns full ordered registry emitted by the configurator
     * @param {String} tableId
     */
    onRouteIndexColumnsUpdate (columns, tableId) {
      this.onColumnsUpdate(columns, tableId)
      this.resetSortWhenColumnHidden()
    },
    /**
     * Clear this table's saved preferences, then re-check the sort for the same
     * reason as above (a reset can only ever reveal columns, but the check is
     * cheap and keeps the two paths symmetrical).
     *
     * @param {String} tableId
     */
    onRouteIndexColumnsReset (tableId) {
      this.onColumnsReset(tableId)
      this.resetSortWhenColumnHidden()
    },
    /**
     * Sorting is done by the API (buildSearchCriteria turns remoteData.sortBy
     * into the `sort` param) and its only control is the arrow in the column
     * header. Hiding the sorted column would leave the list in an order the
     * user can neither explain nor undo, so the sort goes back to the API
     * default and the search runs again. Reordering never touches the sort.
     */
    resetSortWhenColumnHidden () {
      if (this.remoteData.sortBy == null) {
        return
      }
      if (some(this.tableFields, field => field.value === this.remoteData.sortBy)) {
        return
      }
      this.resetRemoteDataTableNavigation()
      this.initiateSearch()
    },
    /**
     * The vehicle's last reported air reading for one sensor, in Celsius as the
     * API sends it (VF-4176), or null when there is nothing to show.
     *
     * @param {RouteRecord} route
     * @param {String} sensor 'freezer' or 'cooler'
     * @return Number|null
     */
    latestAirTemperature (route, sensor) {
      const value = get(route, `latest_air_temperatures.${sensor}.value`)
      return typeof value === 'number' ? value : null
    },
    /**
     * Sets our data table back to its original state before the user-applied filtering and paging changes
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.resetQueryParams()
      this.initiateSearch()
    },
    /**
     * Returns the default search criteria for this data table (DRY)
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        name: null,
        vehicles: [],
        products: [],
        locationCode: null,
        violationLevel: null,
        violations: [],
        violationDuration: null,
        startedAfter: null,
        startedBefore: null,
        deletedRoutes: 'exclude',
        excludeOnTimeViolations: 'false'
      }
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria
     * @return Object
     */
    buildSearchCriteria () {
      const expand = [
        'vehicle',
        'route_products.product',
        'route_products.violations',
        'forecast_route_violations.route_product'
      ]
      const fields = [
        'id',
        'name',
        'start',
        'end',
        'deleted',
        'vehicle.id',
        'vehicle.name',
        'route_products.*',
        'forecast_route_violations.*'
      ]
      // VF-4176: the freezer/cooler air columns are opt-in, but the expand
      // rides the feature flag rather than the column's visibility, so
      // un-hiding a column shows values straight away instead of after the
      // next search. It adds no query: Route::getLatestAirTemperatures() reads
      // vehicles.state, and `vehicle` above is already eager loaded.
      if (this.configurableTableColumnsEnabled) {
        expand.push('latest_air_temperatures')
        fields.push('latest_air_temperatures')
      }
      const searchCriteria = {
        customer_id: this.customerContext.id,
        expand: expand.join(','),
        fields: fields.join(','),
        deleted_scope: this.searchCriteria.deletedRoutes,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.searchCriteria.route_id != null) {
        searchCriteria.route_id = this.searchCriteria.route_id.join(',')
      }
      if (this.searchCriteria.name != null) {
        searchCriteria.name = this.searchCriteria.name
      }
      if (this.searchCriteria.vehicles.length > 0) {
        searchCriteria.vehicle_name = this.searchCriteria.vehicles.join(',')
      }
      if (this.searchCriteria.products.length > 0) {
        searchCriteria.product_id = this.searchCriteria.products.join(',')
      }
      if (this.searchCriteria.locationCode != null) {
        searchCriteria.location_code = this.searchCriteria.locationCode
      }
      if (this.searchCriteria.violationLevel != null) {
        searchCriteria.violation_level = this.searchCriteria.violationLevel
      }
      if (this.searchCriteria.violations.length > 0) {
        searchCriteria.violation_type = this.searchCriteria.violations.join(',')
      }
      if (this.searchCriteria.violationDuration != null) {
        searchCriteria.violation_duration = this.searchCriteria.violationDuration
      }
      if (this.searchCriteria.startedBefore != null) {
        searchCriteria.start_before = this.searchCriteria.startedBefore
      }
      if (this.searchCriteria.startedAfter != null) {
        searchCriteria.start_after = this.searchCriteria.startedAfter
      }
      if (this.searchCriteria.excludeProvisional != null) {
        searchCriteria.exclude_provisional = this.searchCriteria.excludeProvisional
      }
      if (this.searchCriteria.excludeOnTimeViolations != null) {
        searchCriteria.only_late_violations = this.searchCriteria.excludeOnTimeViolations
      }
      if (this.remoteData.sortBy != null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }
      return searchCriteria
    },
    setSearchCriteriaFromQueryParams () {
      const queryParams = this.$route.query
      if (queryParams.startedAfter) {
        this.searchCriteria.startedAfter = queryParams.startedAfter
      }
      if (queryParams.startedBefore) {
        this.searchCriteria.startedBefore = queryParams.startedBefore
      }
      if (queryParams.excludeOnTimeViolations) {
        this.searchCriteria.excludeOnTimeViolations = queryParams.excludeOnTimeViolations
      }
    },
    /**
     * Filter out gps-gap and missed-stop violations. If the customer does not have set point violations enabled out,
     * filter out that as well.
     *
     * @return {Array}
     */
    selectableViolationTypes (violationTypes) {
      remove(violationTypes, violationType => {
        return indexOf([
          RouteViolationRecord.TYPE_SWAPPED_AIR_SENSORS,
          RouteViolationRecord.TYPE_GPS_GAP,
          RouteViolationRecord.TYPE_MISSED_STOP,
          RouteViolationRecord.TYPE_SET_POINT_NOT_REACHED,
          RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED
        ], violationType.name) !== -1
      })
      return violationTypes
    },
    rowClassCallback (row) {
      return row.deleted != null ? 'deleted' : ''
    },
    /**
     * Returns the forecasted violation associated with the route product specified or null if one is not found
     *
     * @param {RouteRecord} route
     * @param {RouteProductRecord} routeProduct
     * @return Object|null
     */
    routeProductForecastViolation (route, routeProduct) {
      if (!routeProduct) {
        return null
      }
      const forecastedRouteViolations = get(route, 'forecast_route_violations', [])
      const forecastedRouteProductViolations = filter(
        forecastedRouteViolations, (forecastViolation) => {
          return forecastViolation.route_product.id === routeProduct.id
        }
      )
      return forecastedRouteProductViolations.length === 1 ? forecastedRouteProductViolations[0] : null
    },
    /**
     * Retreive the vehicle options from the API
     */
    async fetchVehicles () {
      this.vehicleOptionsLoaded = false
      try {
        const response = await this.$orm('Vehicle').findAll({
          customer_id: this.customerContext.id,
          sort: 'name',
          'per-page': 0,
          deleted_scope: 'include'
        })
        this.vehicleOptions = map(response.getItems(), x => {
          let vehicleName = x.name
          if (x.deleted) {
            vehicleName += ' (deleted)'
          }
          return {
            text: vehicleName,
            value: x.name
          }
        })
        this.vehicleOptions = sortBy(this.vehicleOptions, x => x.text)
        this.vehicleOptionsLoaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Retrieve the product options from the API
     */
    async fetchProducts () {
      this.productOptionsLoaded = false
      try {
        const response = await this.$orm('Product').findAll({
          sort: 'zone'
        })
        this.productOptions = map(response.getItems(), x => {
          return {
            text: x.name,
            value: x.id,
            // icon: x.zone === 'cooler' ? 'mdi-thermometer-low' : 'mdi-snowflake',
            color: x.zone
          }
        })
        this.productOptions = sortBy(this.productOptions, x => x.text)
        this.productOptionsLoaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Retrieve the violation options from the API
     */
    async fetchViolations () {
      this.violationOptionsLoaded = false
      try {
        const response = await this.$orm('RouteViolationType').findAll({})
        const violations = this.selectableViolationTypes(response.getItems())
        this.violationOptions = map(violations, x => {
          return {
            text: this.$t(snakeCase(x.name)),
            value: x.name
          }
        })
        this.violationOptions = sortBy(this.violationOptions, x => x.text)
        // Add a no violations option to the top of the list
        this.violationOptions.unshift({ text: this.$t('no_violations'), value: 'none' })
        this.violationOptionsLoaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Retrieve the violation level options from the API.
     * The API filters levels based on customer settings and global feature flags.
     */
    async fetchViolationLevels () {
      this.violationLevelOptionsLoaded = false
      try {
        const response = await this.$orm('RouteViolationLevel').findAll({})
        const relevantLevels = ['fatal', 'forecast', 'forecast_staging']

        this.violationLevelOptions = response.getItems()
          .filter(x => relevantLevels.includes(x.name))
          .map(x => ({
            text: this.$t('violation_level:' + x.name),
            value: x.name
          }))
        this.violationLevelOptionsLoaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Update the violation type options based on the selected violation level.
     * Forecast and forecast_staging violations can only be hot or cold.
     */
    updateViolationTypeOptions (level) {
      if (level === 'forecast' || level === 'forecast_staging') {
        // Forecast violations can only be hot or cold
        this.violationOptions = [
          { text: this.$t('hot'), value: 'hot' },
          { text: this.$t('cold'), value: 'cold' }
        ]
      } else {
        // Reload full list for fatal or no level selected
        this.fetchViolations()
      }
      // Clear incompatible selections
      if (this.searchCriteria.violations.length > 0) {
        const validTypes = this.violationOptions.map(v => v.value)
        this.searchCriteria.violations = this.searchCriteria.violations.filter(
          v => validTypes.includes(v)
        )
      }
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$t('route_list'))
    this.fetchVehicles()
    this.fetchProducts()
    this.fetchViolations()
    this.fetchViolationLevels()
  },
  created () {
    this.setSearchCriteriaFromQueryParams()
  },
  watch: {
    initialized () {
      this.useQueryParamsForSearch()
      this.initiateSearch()
    },
    'searchCriteria.violationLevel': function (newLevel) {
      this.updateViolationTypeOptions(newLevel)
    },
    'searchCriteria.violations': function (newViolations) {
      // Auto-select fatal level if only fatal is available and a violation type is selected
      if (newViolations.length > 0 &&
          this.searchCriteria.violationLevel === null &&
          this.violationLevelOptions.length === 1 &&
          this.violationLevelOptions[0].value === 'fatal') {
        this.searchCriteria.violationLevel = 'fatal'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
a:hover.v-btn {
  text-decoration: none;
}

.v-text-field__details {
  display: none;
}

.not-set {
  color: #9e9e9e;
  font-style: italic;
  text-transform: capitalize;
  white-space: nowrap;
}
</style>
